import { useState, useCallback } from 'react';

const initialFormData = {
  // Step 1: Business Overview
  businessName: '',
  businessType: '',
  yearsInOperation: '',
  monthlyRevenue: '',
  staffCount: '',
  locations: '',
  geography: '',

  // Step 2: Operational Assessment
  role: '',
  painPoints: [],
  hoursOnOps: '',
  supplierCount: '',
  distribution: '',
  previousAttempts: '',

  // Step 3: Goals & Control
  goals: [],
  controlAreas: [],
  ownerCommitment: '',
  whyBliu: '',

  // Step 4: Acknowledgement
  acknowledgements: [],
  email: '',
  phone: '',
};

export function useBriefForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const totalSteps = 4;

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const validateStep = useCallback((stepNumber) => {
    const newErrors = {};

    if (stepNumber === 1) {
      if (!formData.businessName.trim()) newErrors.businessName = 'Required';
      if (!formData.businessType) newErrors.businessType = 'Required';
      if (!formData.yearsInOperation) newErrors.yearsInOperation = 'Required';
      if (!formData.monthlyRevenue) newErrors.monthlyRevenue = 'Required';
      if (!formData.staffCount) newErrors.staffCount = 'Required';
      if (!formData.locations) newErrors.locations = 'Required';
      if (!formData.geography) newErrors.geography = 'Required';

      // Disqualification warning (soft)
      if (formData.yearsInOperation === 'under-1') {
        newErrors.yearsInOperation = 'We typically work with businesses operating 1+ years';
      }
      if (formData.monthlyRevenue === 'under-50k') {
        newErrors.monthlyRevenue = 'We typically work with businesses at $50K+ monthly revenue';
      }
    }

    if (stepNumber === 2) {
      if (!formData.role) newErrors.role = 'Required';
      if (formData.painPoints.length === 0) {
        newErrors.painPoints = 'Select at least one pain point';
      } else if (formData.painPoints.length > 3) {
        newErrors.painPoints = 'Select no more than 3 pain points';
      }
      if (!formData.hoursOnOps) newErrors.hoursOnOps = 'Required';
      if (!formData.supplierCount) newErrors.supplierCount = 'Required';
      if (!formData.distribution) newErrors.distribution = 'Required';
      if (!formData.previousAttempts.trim()) newErrors.previousAttempts = 'Required';
    }

    if (stepNumber === 3) {
      if (formData.goals.length === 0) {
        newErrors.goals = 'Select at least one goal';
      }
      if (formData.controlAreas.length === 0) {
        newErrors.controlAreas = 'Select at least one area';
      }
      if (!formData.ownerCommitment) newErrors.ownerCommitment = 'Required';
      if (!formData.whyBliu.trim()) newErrors.whyBliu = 'Required';
    }

    if (stepNumber === 4) {
      if (formData.acknowledgements.length !== 6) {
        newErrors.acknowledgements = 'All acknowledgements must be accepted';
      }
      if (!formData.email.trim()) {
        newErrors.email = 'Required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Enter a valid email address';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const nextStep = useCallback(() => {
    if (validateStep(step)) {
      setStep((prev) => Math.min(prev + 1, totalSteps));
      window.scrollTo(0, 0);
    }
  }, [step, validateStep]);

  const prevStep = useCallback(() => {
    setStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  }, []);

  const submit = useCallback(async () => {
    if (!validateStep(4)) return;

    setIsSubmitting(true);

    try {
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // In production:
      // await fetch('/api/briefs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData),
      // });

      console.log('Brief submitted:', formData);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Submission failed:', error);
      setErrors({ submit: 'Submission failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateStep]);

  const reset = useCallback(() => {
    setStep(1);
    setFormData(initialFormData);
    setErrors({});
    setIsSubmitting(false);
    setIsSubmitted(false);
  }, []);

  return {
    step,
    totalSteps,
    formData,
    errors,
    isSubmitting,
    isSubmitted,
    updateField,
    nextStep,
    prevStep,
    submit,
    reset,
  };
}

export default useBriefForm;
